import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  DollarSign, 
  TrendingUp, 
  Eye, 
  MousePointerClick,
  ShoppingCart,
  Pencil,
  Save,
  X,
  CreditCard,
  Building,
  Wallet,
  CheckCircle,
  Mail,
  Plus,
  Zap,
  Clock,
  ExternalLink
} from "lucide-react";
import { FaMeta } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CampaignDetail({ user }) {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [campaign, setCampaign] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [packages, setPackages] = useState({});

  const fetchCampaignData = useCallback(async () => {
    try {
      const [campaignRes, metricsRes, paymentsRes] = await Promise.all([
        fetch(`${API}/campaigns/${campaignId}`, { credentials: "include" }),
        fetch(`${API}/campaigns/${campaignId}/metrics?days=30`, { credentials: "include" }),
        fetch(`${API}/payments/campaign/${campaignId}`, { credentials: "include" }),
      ]);

      if (campaignRes.ok) {
        const campaignData = await campaignRes.json();
        setCampaign(campaignData);
        setEditForm({
          daily_budget: campaignData.daily_budget,
          status: campaignData.status,
          revenue: campaignData.revenue,
          ask_price: campaignData.ask_price || 0,
        });
      }
      if (metricsRes.ok) {
        setMetrics(await metricsRes.json());
      }
      if (paymentsRes.ok) {
        setPayments(await paymentsRes.json());
      }
    } catch (error) {
      toast.error("Failed to load campaign");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const fetchPaymentPackages = useCallback(async () => {
    try {
      const response = await fetch(`${API}/payments/packages`, { credentials: "include" });
      if (response.ok) {
        setPackages(await response.json());
      }
    } catch (error) {
      logger.error("Failed to load packages:", error);
    }
  }, []);

  const pollPaymentStatus = useCallback((sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error("Payment verification timed out. Please check your payment history.");
        navigate(`/campaigns/${campaignId}`, { replace: true });
        return;
      }
      attempts += 1;

      try {
        const response = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to check payment status");

        const data = await response.json();

        if (data.payment_status === "paid") {
          toast.success("Payment successful! Your campaign budget has been updated.");
          fetchCampaignData();
          navigate(`/campaigns/${campaignId}`, { replace: true });
          return;
        } else if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          navigate(`/campaigns/${campaignId}`, { replace: true });
          return;
        }

        // Continue polling
        toast.loading("Verifying payment...");
        setTimeout(poll, pollInterval);
      } catch (error) {
        logger.error("Error checking payment status:", error);
        toast.error("Error verifying payment. Please check your payment history.");
        navigate(`/campaigns/${campaignId}`, { replace: true });
      }
    };

    poll();
  }, [campaignId, navigate, fetchCampaignData]);

  useEffect(() => {
    fetchCampaignData();
    fetchPaymentPackages();
  }, [fetchCampaignData, fetchPaymentPackages]);

  // Handle Stripe payment return (runs on mount + when params change)
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (paymentStatus === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.error("Payment was cancelled");
      navigate(`/campaigns/${campaignId}`, { replace: true });
    }
  }, [searchParams, campaignId, navigate, pollPaymentStatus]);

  const handleFundCampaign = async () => {
    if (!selectedPackage && !customAmount) {
      toast.error("Please select a package or enter a custom amount");
      return;
    }

    setProcessingPayment(true);

    try {
      const originUrl = window.location.origin;
      let endpoint = `${API}/payments/checkout`;
      let body = {};

      if (selectedPackage) {
        body = {
          package_id: selectedPackage,
          campaign_id: campaignId,
          origin_url: originUrl,
        };
      } else {
        endpoint = `${API}/payments/checkout/custom`;
        body = {
          amount: parseFloat(customAmount),
          campaign_id: campaignId,
          origin_url: originUrl,
        };
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to initiate payment");
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      toast.error(error.message);
      setProcessingPayment(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API}/campaigns/${campaignId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editForm),
      });

      if (response.ok) {
        const updated = await response.json();
        setCampaign({ ...campaign, ...updated });
        setEditing(false);
        toast.success("Campaign updated");
      } else {
        toast.error("Failed to update campaign");
      }
    } catch (error) {
      toast.error("Failed to update campaign");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  const getRoasColor = (roas) => {
    if (roas >= 1.75) return "text-emerald-400";
    if (roas >= 1.4) return "text-amber-400";
    return "text-red-400";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout user={user}>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <p className="text-zinc-400 mb-4">Campaign not found</p>
          <Button onClick={() => navigate("/campaigns")}>Go back to Campaigns</Button>
        </div>
      </DashboardLayout>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#18181B] border border-white/10 rounded-lg p-4 shadow-xl">
          <p className="text-zinc-400 text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={entry.dataKey || entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name === "ROAS" ? entry.value.toFixed(2) : formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <DashboardLayout user={user}>
      <div data-testid="campaign-detail-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/campaigns")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              {campaign.platform === "meta" ? (
                <FaMeta className="w-6 h-6 text-[#1877F2]" />
              ) : (
                <FcGoogle className="w-6 h-6" />
              )}
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl text-white font-bold">
                  {campaign.name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge 
                    className={cn(
                      "capitalize",
                      campaign.status === "active" 
                        ? "bg-emerald-500/10 text-emerald-400" 
                        : "bg-amber-500/10 text-amber-400"
                    )}
                  >
                    {campaign.status}
                  </Badge>
                  <span className="text-zinc-400 text-sm capitalize">{campaign.platform}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              data-testid="fund-campaign-btn"
              onClick={() => setFundDialogOpen(true)}
              className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Fund Campaign
            </Button>
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setEditing(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setEditing(true)}
                className="bg-white/10 text-white hover:bg-white/20 border border-white/10"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit Campaign
              </Button>
            )}
          </div>
        </div>

        {/* Fund Campaign Dialog */}
        <Dialog open={fundDialogOpen} onOpenChange={setFundDialogOpen}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-emerald-400" />
                Fund Campaign
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Add budget to your campaign via Stripe secure checkout
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Budget Packages */}
              <div className="space-y-3">
                <Label className="text-zinc-300">Select a Budget Package</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(packages).map(([id, pkg]) => (
                    <button
                      key={id}
                      data-testid={`package-${id}`}
                      onClick={() => {
                        setSelectedPackage(id);
                        setCustomAmount("");
                      }}
                      className={cn(
                        "p-4 rounded-xl border text-left transition-all",
                        selectedPackage === id
                          ? "border-emerald-500 bg-emerald-500/10"
                          : "border-white/10 bg-white/5 hover:border-white/20"
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-white">{pkg.name}</span>
                        {selectedPackage === id && (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        )}
                      </div>
                      <p className="text-2xl font-heading font-bold text-white">
                        ${pkg.amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-zinc-400 mt-1">{pkg.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Or Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-zinc-400 text-sm">OR</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Custom Amount */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Enter Custom Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    data-testid="custom-amount-input"
                    type="number"
                    min="10"
                    max="50000"
                    placeholder="Enter amount ($10 - $50,000)"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedPackage(null);
                    }}
                    className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                  />
                </div>
              </div>

              {/* Payment Info */}
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-blue-300 font-medium">Secure Payment via Stripe</p>
                    <p className="text-blue-300/70 mt-1">
                      Your payment is processed securely. Budget will be added immediately upon successful payment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={() => setFundDialogOpen(false)}
                className="text-zinc-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="proceed-to-payment-btn"
                onClick={handleFundCampaign}
                disabled={processingPayment || (!selectedPackage && !customAmount)}
                className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
              >
                {processingPayment ? (
                  <div className="spinner w-4 h-4 mr-2 border-white border-t-transparent" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Proceed to Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Form */}
        {editing && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Edit Campaign</CardTitle>
                <CardDescription className="text-zinc-400">
                  Update budget, status, and revenue data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Daily Budget ($)</Label>
                    <Input
                      type="number"
                      value={editForm.daily_budget}
                      onChange={(e) => setEditForm({ ...editForm, daily_budget: parseFloat(e.target.value) })}
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Ask Price ($)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        data-testid="ask-price-input"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Price per conversion"
                        value={editForm.ask_price}
                        onChange={(e) => {
                          const askPrice = parseFloat(e.target.value) || 0;
                          const newRevenue = askPrice * (campaign.conversions || 0);
                          setEditForm({ ...editForm, ask_price: askPrice, revenue: newRevenue });
                        }}
                        className="pl-10 bg-black/50 border-white/10 text-white"
                      />
                    </div>
                    <p className="text-zinc-500 text-xs">
                      Revenue per conversion (e.g., avg order value)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Revenue ($)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        data-testid="revenue-input"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter actual revenue"
                        value={editForm.revenue}
                        onChange={(e) => setEditForm({ ...editForm, revenue: parseFloat(e.target.value) || 0 })}
                        className="pl-10 bg-black/50 border-white/10 text-white"
                      />
                    </div>
                    <p className="text-zinc-500 text-xs">
                      {editForm.ask_price > 0 
                        ? `Calculated: ${campaign.conversions || 0} conversions × $${editForm.ask_price}`
                        : "Or enter total revenue directly"}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Status</Label>
                    <div className="flex items-center gap-3 h-10">
                      <Switch
                        checked={editForm.status === "active"}
                        onCheckedChange={(checked) => 
                          setEditForm({ ...editForm, status: checked ? "active" : "paused" })
                        }
                      />
                      <span className="text-white capitalize">{editForm.status}</span>
                    </div>
                  </div>
                </div>
                
                {/* ROAS Preview */}
                {editForm.revenue !== campaign.revenue && campaign.total_spend > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-emerald-300 text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      New ROAS will be: <strong>{(editForm.revenue / campaign.total_spend).toFixed(2)}x</strong>
                      <span className="text-zinc-400">
                        (currently {campaign.roas.toFixed(2)}x)
                      </span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-zinc-400" />
              <span className="text-xs uppercase tracking-widest text-zinc-400">Daily Budget</span>
            </div>
            <p className="font-heading text-2xl text-white font-bold tabular-nums">
              {formatCurrency(campaign.daily_budget)}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-zinc-400" />
              <span className="text-xs uppercase tracking-widest text-zinc-400">Total Spend</span>
            </div>
            <p className="font-heading text-2xl text-white font-bold tabular-nums">
              {formatCurrency(campaign.total_spend)}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart className="w-4 h-4 text-zinc-400" />
              <span className="text-xs uppercase tracking-widest text-zinc-400">Revenue</span>
            </div>
            <p className="font-heading text-2xl text-white font-bold tabular-nums">
              {formatCurrency(campaign.revenue)}
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-zinc-400" />
              <span className="text-xs uppercase tracking-widest text-zinc-400">ROAS</span>
            </div>
            <p className={cn("font-heading text-2xl font-bold tabular-nums", getRoasColor(campaign.roas))}>
              {campaign.roas.toFixed(2)}x
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <MousePointerClick className="w-4 h-4 text-zinc-400" />
              <span className="text-xs uppercase tracking-widest text-zinc-400">Conversions</span>
            </div>
            <p className="font-heading text-2xl text-white font-bold tabular-nums">
              {formatNumber(campaign.conversions)}
            </p>
          </motion.div>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
            <span className="text-xs uppercase tracking-widest text-zinc-400">Impressions</span>
            <p className="font-heading text-xl text-white font-bold tabular-nums mt-2">
              {formatNumber(campaign.impressions)}
            </p>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
            <span className="text-xs uppercase tracking-widest text-zinc-400">Clicks</span>
            <p className="font-heading text-xl text-white font-bold tabular-nums mt-2">
              {formatNumber(campaign.clicks)}
            </p>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
            <span className="text-xs uppercase tracking-widest text-zinc-400">CTR</span>
            <p className="font-heading text-xl text-white font-bold tabular-nums mt-2">
              {campaign.ctr.toFixed(2)}%
            </p>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
            <span className="text-xs uppercase tracking-widest text-zinc-400">CPC</span>
            <p className="font-heading text-xl text-white font-bold tabular-nums mt-2">
              ${campaign.cpc.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Payment History Card */}
        {payments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
          >
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-400" />
                  Payment History
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Recent payments for this campaign
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {payments.slice(0, 5).map((payment) => (
                    <div
                      key={payment.transaction_id}
                      className="flex items-center justify-between p-4 rounded-lg bg-white/5"
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center",
                          payment.payment_status === "paid"
                            ? "bg-emerald-500/10"
                            : payment.payment_status === "pending" || payment.payment_status === "initiated"
                            ? "bg-amber-500/10"
                            : "bg-red-500/10"
                        )}>
                          {payment.payment_status === "paid" ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : payment.payment_status === "pending" || payment.payment_status === "initiated" ? (
                            <Clock className="w-5 h-5 text-amber-400" />
                          ) : (
                            <X className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {formatCurrency(payment.amount)}
                          </p>
                          <p className="text-zinc-400 text-sm">
                            {payment.package_id === "custom" ? "Custom Amount" : packages[payment.package_id]?.name || payment.package_id}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={cn(
                          "capitalize",
                          payment.payment_status === "paid"
                            ? "bg-emerald-500/10 text-emerald-400"
                            : payment.payment_status === "pending" || payment.payment_status === "initiated"
                            ? "bg-amber-500/10 text-amber-400"
                            : "bg-red-500/10 text-red-400"
                        )}>
                          {payment.payment_status}
                        </Badge>
                        <p className="text-zinc-400 text-xs mt-1">
                          {formatDate(payment.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Payment Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-400" />
                Billing Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    {campaign.payment_method === "credit_card" && <CreditCard className="w-6 h-6 text-emerald-400" />}
                    {campaign.payment_method === "bank_transfer" && <Building className="w-6 h-6 text-emerald-400" />}
                    {campaign.payment_method === "paypal" && <Wallet className="w-6 h-6 text-emerald-400" />}
                    {!campaign.payment_method && <CreditCard className="w-6 h-6 text-emerald-400" />}
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-400">Payment Method</p>
                    <p className="text-white font-medium capitalize">
                      {campaign.payment_method?.replace("_", " ") || "Credit Card"}
                    </p>
                    {campaign.card_last_four && (
                      <p className="text-zinc-400 text-sm">•••• {campaign.card_last_four}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5">
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-400">Billing Email</p>
                    <p className="text-white font-medium">
                      {campaign.billing_email || user?.email || "Not set"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5">
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center",
                    campaign.payment_status === "active" || !campaign.payment_status
                      ? "bg-emerald-500/10"
                      : "bg-red-500/10"
                  )}>
                    <CheckCircle className={cn(
                      "w-6 h-6",
                      campaign.payment_status === "active" || !campaign.payment_status
                        ? "text-emerald-400"
                        : "text-red-400"
                    )} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-400">Payment Status</p>
                    <p className={cn(
                      "font-medium capitalize",
                      campaign.payment_status === "active" || !campaign.payment_status
                        ? "text-emerald-400"
                        : "text-red-400"
                    )}>
                      {campaign.payment_status || "Active"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Performance History (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={metrics}>
                    <defs>
                      <linearGradient id="colorRevenue2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorSpend2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => <span className="text-zinc-400">{value}</span>}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Revenue"
                      stroke="#10B981" 
                      fill="url(#colorRevenue2)"
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="spend" 
                      name="Spend"
                      stroke="#3B82F6" 
                      fill="url(#colorSpend2)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* ROAS Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">ROAS Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metrics.map(m => ({
                    ...m,
                    roas: m.spend > 0 ? (m.revenue / m.spend) : 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      domain={[0, 'auto']}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181B', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [value.toFixed(2) + 'x', 'ROAS']}
                      labelStyle={{ color: '#71717a' }}
                    />
                    {/* Threshold line */}
                    <Line 
                      type="monotone" 
                      dataKey={() => 1.75}
                      name="Threshold"
                      stroke="#F59E0B"
                      strokeDasharray="5 5"
                      strokeWidth={1}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="roas" 
                      name="ROAS"
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={{ fill: '#10B981', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-zinc-400 text-sm mt-4">
                <span className="inline-block w-4 h-0.5 bg-amber-500 mr-2" />
                ROAS Threshold: 1.75x
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
